import { Suspense } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import "./i18n";
import { captureAttribution } from "./lib/attribution";

// Sentry browser error monitoring
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Filter out well-known third-party browser noise that doesn't represent
    // real user-facing bugs.
    ignoreErrors: [
      // Microsoft Outlook SafeLinks pre-fetch (CefSharp-based crawler) injects
      // a JS bridge that fails silently.
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
      // ResizeObserver loop limit / loop completed with undelivered
      // notifications: benign browser warning.
      /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
      // Facebook/Instagram in-app browser bridge noise.
      "fb_xd_fragment",
      // MetaMask / other Web3 wallet browser extensions auto-inject into every
      // page and throw connect errors on sites that don't use Web3.
      /Failed to connect to MetaMask/,
      /MetaMask extension not found/,
      // Browser-translator DOM mutation crashes. When Chrome/Edge/Safari
      // auto-translate the page, they replace text nodes with translated
      // equivalents. React's commit phase then tries to update/unmount the
      // original node and the parent no longer owns it, throwing NotFoundError
      // or HierarchyRequestError. Caught by ErrorBoundary so the page recovers;
      // not actionable in our code. Mirrors a long-standing React/Google-Translate
      // interaction: https://github.com/facebook/react/issues/11538
      /Failed to execute 'removeChild' on 'Node'/,
      /Failed to execute 'insertBefore' on 'Node'/,
      /The node to be removed is not a child of this node/,
      /The node before which the new node is to be inserted is not a child of this node/,
      // Browser extensions / in-app browsers JSON.stringify a React-managed DOM node whose __reactFiber back-reference is circular, so V8 throws. Not first-party (we only stringify plain payloads). Mirrors wc-calendar's WC-CALENDAR-G filter.
      /Converting circular structure to JSON/,
    ],
    // Drop any event whose top stack frame originates inside a browser
    // extension. Covers MetaMask, ad blockers, password managers, etc.
    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],
  });
}

// Migrate legacy hash URLs (e.g. /#/calendar?token=abc) to clean paths
if (window.location.hash.startsWith("#/")) {
  const hashPath = window.location.hash.slice(1); // "#/calendar?x=y" -> "/calendar?x=y"
  history.replaceState(null, "", hashPath);
}

// Backwards compat: redirect ?lang=xx to /xx/ path (client-side fallback
// in case middleware didn't catch it, e.g. dev mode)
const langParam = new URLSearchParams(window.location.search).get("lang");
if (langParam) {
  const params = new URLSearchParams(window.location.search);
  params.delete("lang");
  const remaining = params.toString();
  const basePath = window.location.pathname === "/" ? "" : window.location.pathname;
  const newPath = langParam === "en"
    ? `${basePath || "/"}${remaining ? "?" + remaining : ""}`
    : `/${langParam}${basePath}${remaining ? "?" + remaining : ""}`;
  window.location.replace(newPath);
}

// Capture UTM / Reddit Click ID attribution from URL params
captureAttribution();

createRoot(document.getElementById("root")!).render(
  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
    <App />
  </Suspense>
);
